import axios from 'axios';
import keycloak from '../security/keycloak';

const defaultBaseUrl = process.env.REACT_APP_API_URL;
const DEM_PRESIGN_BATCH_SIZE = 100;
const DEM_UPLOAD_CONCURRENCY = 4;

export async function getFreshAccessToken(minValiditySeconds = 60) {
  if (keycloak?.token && keycloak?.updateToken) {
    await keycloak.updateToken(minValiditySeconds);
    localStorage.setItem('jwtToken', keycloak.token);
    return keycloak.token;
  }

  const token = localStorage.getItem('jwtToken');
  if (token) return token;

  throw new Error('No Keycloak access token available.');
}

async function refreshAccessTokenImmediately() {
  if (!keycloak?.updateToken) {
    throw new Error('Keycloak token refresh is not available.');
  }

  await keycloak.updateToken(-1);
  localStorage.setItem('jwtToken', keycloak.token);
  return keycloak.token;
}

export async function DBRequest({
  baseUrl = defaultBaseUrl,
  path,
  data = {},
  method = 'get',
  onResponse = () => {},
  onError = () => {},
  onLoading = () => {},
}) {
  onLoading(true);
  try {
    const token = await getFreshAccessToken();
    const response = await axios({
      method: method,
      url: baseUrl + path,
      data: data,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    onResponse(response.data);
  } catch (error) {
    console.error('Error fetching data:', error);
    onError(error);
  } finally {
    onLoading(false);
  }
}

export function DBUploadDemViaBackend({
  baseUrl = defaultBaseUrl,
  projectId,
  file,
  onProgressChange = () => {},
  onTransferComplete = () => {},
  onError = () => {},
}) {
  const abortController = new AbortController();

  const run = async () => {
    try {
      if (!file) throw new Error('No DEM file selected.');

      const formData = new FormData();
      formData.append('file', file, file.name);

      const send = async (token) =>
        axios.post(
          `${baseUrl}project/dem_upload/${projectId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: abortController.signal,
            onUploadProgress: (event) => {
              if (!event.total) {
                onProgressChange('Uploading file...');
                return;
              }
              const percent = Math.round((event.loaded / event.total) * 100);
              onProgressChange(`Uploading ${percent} %`);
            },
          }
        );

      onProgressChange('Uploading file...');
      let response;
      try {
        response = await send(await getFreshAccessToken());
      } catch (error) {
        if (error?.response?.status !== 401) throw error;
        response = await send(await refreshAccessTokenImmediately());
      }

      const upload = response.data?.dem_upload;
      if (!upload?.id) {
        throw new Error('DEM upload response did not include an upload ID.');
      }
      onTransferComplete(upload);
      return upload;
    } catch (error) {
      if (!abortController.signal.aborted) {
        onError(normalizeError(error, 'DEM upload failed.'));
      }
      return null;
    }
  };

  const promise = run();
  return {
    promise,
    abort: () => abortController.abort(),
  };
}

export function DBUploadDataInChunks({
  baseUrl = defaultBaseUrl,
  path,
  file,
  chunkSize,
  onProgressChange = () => {},
  onFinished = () => {},
  onResponse,
  onFeedback = () => {},
  debug = false,
  onError = () => {},
  maxRetries = 3, // Add a maximum retry count
}) {
  const uploadFileInChunks = async (file, chunkSize, uploadUrl) => {
    const buildUploadHeaders = async () => {
      const token = await getFreshAccessToken();
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      };
    };

    try {
      const totalChunks = Math.ceil(file.size / chunkSize);

      const sendChunk = async (start, end, index, retries = 0, authRetried = false) => {
        const formData = new FormData();
        const blobSlice = file.slice(start, end);
        formData.append('file', blobSlice, file.name);
        formData.append('dzchunkbyteoffset', start);
        formData.append('dztotalchunkcount', totalChunks);
        formData.append('dzchunksize', chunkSize);
        formData.append('dztotalfilesize', file.size);
        formData.append('dzchunkindex', index);

        const headers = await buildUploadHeaders();

        try {
          const response = await axios.post(uploadUrl, formData, {
            headers,
          });
          if (totalChunks - 1 === index) onResponse(response.data);
        } catch (error) {
          if (error?.response?.status === 401 && !authRetried) {
            await refreshAccessTokenImmediately();
            await sendChunk(start, end, index, retries, true);
            return;
          }

          if (retries < maxRetries) {
            console.warn(
              `Chunk ${index} failed, retrying... (${retries + 1}/${maxRetries})`
            );
            await sendChunk(start, end, index, retries + 1, authRetried); // Retry the chunk
          } else {
            console.error(`Chunk ${index} failed after ${maxRetries} retries.`);
            throw error;
          }
        }
      };

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end =
          (i + 1) * chunkSize > file.size ? file.size : (i + 1) * chunkSize; // Adjust end for the last chunk
        await sendChunk(start, end, i); // Handle retries within sendChunk
        const percent = Math.round(((i + 1) / (totalChunks - 1)) * 100) + ' %';
        onProgressChange(
          totalChunks - 1 === i + 1 ? 'Processing data' : percent
        ); // Update progress
      }

      onFinished(true);
    } catch (error) {
      onError(error);
      console.error('Error during chunk upload:', error);
    }
  };

  return uploadFileInChunks(file, chunkSize, baseUrl + path);
}

function normalizeError(error, fallbackMessage) {
  if (error?.response?.data?.message) {
    return new Error(error.response.data.message);
  }
  if (error instanceof Error) return error;
  return new Error(fallbackMessage);
}

async function demApiRequest({ baseUrl, path, method = 'get', data }) {
  const request = async (token) =>
    axios({
      method,
      url: baseUrl + path,
      data,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

  try {
    return (await request(await getFreshAccessToken())).data;
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    return (await request(await refreshAccessTokenImmediately())).data;
  }
}

async function uploadDemPart({
  file,
  part,
  upload,
  maxRetries,
  shouldAbort,
}) {
  const start = (part.part_number - 1) * upload.part_size_bytes;
  const end = Math.min(start + upload.part_size_bytes, file.size);
  const blob = file.slice(start, end);

  for (let retry = 0; retry <= maxRetries; retry += 1) {
    if (shouldAbort()) throw new Error('Upload aborted.');

    try {
      const response = await axios.put(part.url, blob, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });
      const etag = response.headers?.etag || response.headers?.ETag;
      if (!etag) {
        throw new Error('S3 upload response did not include an ETag.');
      }
      return { part_number: part.part_number, etag };
    } catch (error) {
      if (retry >= maxRetries) throw error;
    }
  }

  throw new Error('Could not upload DEM part.');
}

async function uploadDemParts({
  file,
  upload,
  parts,
  concurrency,
  maxRetries,
  shouldAbort,
  onPartUploaded,
}) {
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < parts.length) {
      if (shouldAbort()) return;
      const part = parts[nextIndex];
      nextIndex += 1;
      const completedPart = await uploadDemPart({
        file,
        part,
        upload,
        maxRetries,
        shouldAbort,
      });
      onPartUploaded(completedPart);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, parts.length) }, () => worker())
  );
}

export function DBUploadDemMultipart({
  baseUrl = defaultBaseUrl,
  projectId,
  file,
  concurrency = DEM_UPLOAD_CONCURRENCY,
  onProgressChange = () => {},
  onTransferComplete = () => {},
  onError = () => {},
  maxRetries = 3,
}) {
  let aborted = false;
  let uploadId = null;
  let completed = false;
  const shouldAbort = () => aborted;

  const api = ({ path, method, data }) =>
    demApiRequest({ baseUrl, path, method, data });

  const abortRemoteUpload = async () => {
    if (!uploadId || completed) return;
    try {
      await api({
        method: 'delete',
        path: `projects/${projectId}/dem-uploads/${uploadId}`,
      });
    } catch (error) {
      console.error('Error aborting DEM upload:', error);
    }
  };

  const run = async () => {
    try {
      if (!file) throw new Error('No DEM file selected.');

      onProgressChange('Initializing upload');
      const upload = await api({
        method: 'post',
        path: `projects/${projectId}/dem-uploads`,
        data: { filename: file.name, size_bytes: file.size },
      });
      uploadId = upload.id;
      if (shouldAbort()) throw new Error('Upload aborted.');

      const partCount =
        upload.part_count ||
        Math.ceil(upload.size_bytes / upload.part_size_bytes);
      const partNumbers = Array.from(
        { length: partCount },
        (_, index) => index + 1
      );
      const uploadedParts = [];

      for (
        let index = 0;
        index < partNumbers.length;
        index += DEM_PRESIGN_BATCH_SIZE
      ) {
        if (shouldAbort()) throw new Error('Upload aborted.');
        const batch = partNumbers.slice(
          index,
          index + DEM_PRESIGN_BATCH_SIZE
        );
        const presigned = await api({
          method: 'post',
          path: `projects/${projectId}/dem-uploads/${upload.id}/parts/presign`,
          data: { part_numbers: batch },
        });

        await uploadDemParts({
          file,
          upload,
          parts: presigned.parts,
          concurrency,
          maxRetries,
          shouldAbort,
          onPartUploaded: (completedPart) => {
            uploadedParts.push(completedPart);
            const percent = Math.round(
              (uploadedParts.length / partCount) * 100
            );
            onProgressChange(`Uploading ${percent} %`);
          },
        });
      }

      if (shouldAbort()) throw new Error('Upload aborted.');
      onProgressChange('Completing upload');
      const completedUpload = await api({
        method: 'post',
        path: `projects/${projectId}/dem-uploads/${upload.id}/complete`,
        data: {
          parts: uploadedParts.sort(
            (first, second) => first.part_number - second.part_number
          ),
        },
      });
      completed = true;
      onTransferComplete(completedUpload);
      return completedUpload;
    } catch (error) {
      await abortRemoteUpload();
      if (!shouldAbort()) {
        onError(normalizeError(error, 'DEM upload failed.'));
      }
      return null;
    }
  };

  const promise = run();
  return {
    promise,
    abort: () => {
      aborted = true;
      abortRemoteUpload();
    },
  };
}
